import React, { useState, useEffect, useReducer, useRef, useMemo } from "react";
import {
  Tabs,
  Tab,
  Grid,
  Box,
  Card,
  IconButton,
  Input,
  InputAdornment,
  Typography,
  Stack,
  TextField,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridToolbarColumnsButton,
  GridRowModes,
  useGridApiRef,
} from "@mui/x-data-grid";
import { I18n } from "@aws-amplify/core";
import { get } from "aws-amplify/api";
import { fetchAuthSession } from "@aws-amplify/core";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import currentUserToken from "CurrentUserToken";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
// import EditUser from "popup/EditUser";
import EditPermission from "./EditPermission";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import columns from "./columns";
import FullscreenLoader from "components/FullscreenLoader";

const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    return { audit: action.value, fetched: true };
  }
  return lastState;
};

function Profile() {
  const [state, dispatch] = useReducer(reducer, { 
    audit: [], 
    fetched: false 
  });

  /* -------------------------------------------------- USESTATE -------------------------------------------*/
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editPermissionOpen, setPermissionOpen] = useState(false);
  const [resizeThrottle, setResizeThrottle] = useState(60);
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [tableHeight, setTableHeight] = useState(0);
  const [groupNameSel, setGroupNameSel] = useState(""); // State to store selected group name
  const [profileUser, setProfileUser] = useState("");
  const [profilePermission, setProfilePermission] = useState("");
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || "en"
  );
  const [tableParentIsMD, setTableParentIsMD] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const toolbarRef = useRef(null);
  const [userInfo, setUserInfo] = useState({
    email: "",
    name: "",
    profiles: ["NOT ADMIN"],
  });

  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const [rowModesModel, setRowModesModel] = useState({});
  const [fullLoading, setFullLoading] = useState(false);

  /* -------------------------------------------------------- API -------------------------------------------*/
  useEffect(() => {
    async function currentSession() {
      try {
        const { accessToken, idToken } =
          (await fetchAuthSession()).tokens ?? {};

        setUserInfo({
          name: idToken.payload.name || "No Name",
          email: idToken.payload.email || "No email",
          profiles: idToken.payload["cognito:groups"] || [],
        });
        // Save profiles to setProfile
      } catch (err) {
        console.error("Error fetching current session:", err);
      }
    }

    currentSession();
  }, []);

  const hrGetAllUsers = async () => {
    setFullLoading(true);
    try {
      const restOperation = get({
        apiName: "hrAPI",
        path: "/userManagement/hrUser/list/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();
      if (response.length > 0) {
        const userProfile = response[0].user_profile; // Adjust based on your API response structure
        setProfilePermission(userProfile);
      }
      dispatch({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Errore durante la chiamata API:", error);
    } finally {
      setFullLoading(false);
    }
  };

  useEffect(() => {
    hrGetAllUsers();
  }, []);

/* -------------------------------------------------------- HANDLERS -------------------------------------------*/
  const handleSaveUser = (updatedUser) => {
    const updatedAudit = state.audit.map((user) =>
      user.user_id === updatedUser.user_id ? updatedUser : user
    );
    dispatch({ type: "DATA_RECEIVED", value: updatedAudit });
  };

  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const hiddenColumns = JSON.parse(
      localStorage.getItem("hiddenColumns") || "[]"
    );
    if (apiRef.current) {
      apiRef.current.setColumnVisibilityModel(
        hiddenColumns.reduce((model, field) => {
          model[field] = false;
          return model;
        }, {})
      );
    }
  }, [apiRef]);

  const handleColumnVisibilityChange = (newModel) => {
    const hiddenColumns = Object.keys(newModel).filter(
      (field) => !newModel[field]
    );
    localStorage.setItem("hiddenColumns", JSON.stringify(hiddenColumns));
  };

/* ---------------------------------------------------- BROWSER VIEW -------------------------------------------*/
  function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) {
      return "Firefox";
    } else if (userAgent.includes("Edg")) {
      return "Edge";
    }
  }

  const browser = getBrowserName();

  useEffect(() => {
    if (browser === "Firefox") {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "auto";
      };
    }
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      const navbarHeight = document.getElementById("navbar");
      const footerHeight = document.getElementById("footer");
      const availableHeight =
        document.documentElement.clientHeight -
        navbarHeight -
        footerHeight -
        90;
      setTableHeight(availableHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  
  /* -------------------------------------------------------- FILTERS -------------------------------------------*/
  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };

  const filteredRows = state.audit.filter((value) => {
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      return Object.values(value).some((val) =>
        String(val).toLowerCase().includes(searchTextLower)
      );
    }
    return true;
  });

  const filteredRowsCount = filteredRows.length;

  /* -------------------------------------------------------- JSX -------------------------------------------*/
  return (
    <>
      <DashboardLayout>
        <DashboardNavbar
          absolute
          light
          language={language}
          setLanguage={setLanguage}
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ p: 2, maxHeight: `${tableHeight}px` }}>
              <Card
                sx={{
                  p: 3,
                  maxHeight: `${tableHeight}px`,
                }}
              >
                <Stack
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <FullscreenLoader loading={fullLoading} />
                  <Box>
                    <Typography variant="h4">
                      {I18n.get("Users List")} ({filteredRows.length})
                    </Typography>
                  </Box>
                  <Box>
                    <Input
                      type="text"
                      placeholder={I18n.get("Search")}
                      onChange={searchChanged}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "12px 0px  12px  12px  ",
                        },
                        "& .MuiFormControl-root-MuiTextField-root": {
                          marginTop: "0px",
                        },
                        maxWidth: "200px",
                        mb: 2,
                        mt: 2,
                      }}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton>
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  </Box>
                </Stack>

                <DataGrid
                  rows={filteredRows}
                  columns={columns(setSelectedUser, setPermissionOpen, userInfo)} // Pass userInfo to columns
                  getRowId={(row) => row.user_id}
                  rowModesModel={rowModesModel}
                  onRowModesModelChange={(newModel) =>
                    setRowModesModel(newModel)
                  }
                  onColumnVisibilityModelChange={handleColumnVisibilityChange}
                  initialState={{
                    // density: "compact",
                    pinnedColumns: { right: ["actions"] },
                    sorting: {
                      sortModel: [{ field: "user_email", sort: "asc" }],
                    },
                  }}
                  apiRef={apiRef}
                  hideFooter
                  sx={{ minHeight: "200px" }}
                  resizeThrottleMs={resizeThrottle}
                  loading={!state.fetched || resizeThrottle > 60}
                  slotProps={{
                    loadingOverlay: {
                      variant: "skeleton",
                      noRowsVariant: "skeleton",
                    },
                  }}
                />
              </Card>
            </Box>
          </Grid>
        </Grid>
        
      </DashboardLayout>
      {/* {selectedUser && (
        <EditUser
          open={editUserOpen}
          onClose={() => {
            setEditUserOpen(false);
            tlmGetAllUsers();
          }}
          userData={selectedUser}
          onSave={handleSaveUser}
          profileUser={profileUser}
          setProfileUser={setProfileUser}
        />
      )} */}
      {editPermissionOpen && (
        <EditPermission
          open={editPermissionOpen}
          onClose={() => {
            setPermissionOpen(false);
          }}
          userData={selectedUser}
          onSave={handleSaveUser}
          profileUser={profileUser}
          setProfileUser={setProfileUser}
        />
      )}
    </>
  );
}
export default Profile;
